/**
 * useAddWorkflow — single hook for the /add capture workflow (ported from
 * Noteble — ADR-022).
 *
 * Owns:
 * - useReducer for all workflow state (step, note, streams, templates)
 * - localStorage persistence (auto-save on change, restore on mount)
 * - Convenience action creators
 */

import { useReducer, useState, useEffect, useRef, useCallback } from 'react';
import type { TemplateMetadata } from '@/services/templatesApi';
import type { InputMethod, WorkflowContext, WorkflowEvent, WorkflowStep } from './types';
import { workflowReducer } from './reducer';
import { initialContext } from './transitions';
import { serializeWorkflow, deserializeWorkflow, clearPersistedWorkflow } from './persistence';

export type { WorkflowContext, WorkflowEvent, WorkflowStep };

export interface AddWorkflowReturn {
  ctx: WorkflowContext;
  dispatch: React.Dispatch<WorkflowEvent>;
  clearPersistedState: () => void;

  // ── Convenience actions ──
  setInputMethod: (method: InputMethod) => void;
  setNote: (text: string) => void;
  goToStep: (step: WorkflowStep) => void;
  toggleTemplate: (template: TemplateMetadata) => void;
  setTemplates: (templates: TemplateMetadata[]) => void;
  startOver: () => void;
  setError: (error: string | null) => void;

  /** True when the workflow was restored from localStorage (e.g. after refresh). */
  wasRestoredFromPersistence: boolean;

  /** Template ids restored before the template list loaded — resolved by the
   *  template step once templates arrive. */
  pendingTemplateIdsRef: React.MutableRefObject<string[] | null>;
}

export function useAddWorkflow(params: { templates: TemplateMetadata[]; templatesLoaded: boolean }): AddWorkflowReturn {
  const { templates, templatesLoaded } = params;
  const [ctx, dispatch] = useReducer(workflowReducer, initialContext);

  const pendingTemplateIdsRef = useRef<string[] | null>(null);

  // ── Persistence: restore on mount (once templates have loaded, so the
  // selected-template references can be resolved) ──
  const hasRestoredRef = useRef(false);
  const [wasRestoredFromPersistence, setWasRestoredFromPersistence] = useState(false);

  useEffect(() => {
    if (hasRestoredRef.current || !templatesLoaded) return;
    hasRestoredRef.current = true;

    const restored = deserializeWorkflow(templates);
    if (restored) {
      dispatch({ type: 'RESTORE_WORKFLOW', payload: restored.context });
      if (restored.pendingTemplateIds.length > 0) {
        pendingTemplateIdsRef.current = restored.pendingTemplateIds;
      }
      setWasRestoredFromPersistence(true);
    }
  }, [templates, templatesLoaded]);

  // ── Persistence: auto-save on workflow context change ──
  useEffect(() => {
    if (!hasRestoredRef.current) return;
    serializeWorkflow(ctx);
  }, [ctx.step, ctx.inputMethod, ctx.note, ctx.capturedStreams, ctx.selectedTemplates, ctx.activeDraftId]);

  // ── Convenience actions ──
  const setInputMethod = useCallback((method: InputMethod) => dispatch({ type: 'SET_INPUT_METHOD', method }), []);
  const setNote = useCallback((text: string) => dispatch({ type: 'SET_NOTE', text }), []);
  const goToStep = useCallback((step: WorkflowStep) => dispatch({ type: 'GOTO_STEP', step }), []);
  const toggleTemplate = useCallback((template: TemplateMetadata) => dispatch({ type: 'TOGGLE_TEMPLATE', template }), []);
  const setTemplates = useCallback((tpls: TemplateMetadata[]) => dispatch({ type: 'SET_TEMPLATES', templates: tpls }), []);
  const startOver = useCallback(() => {
    dispatch({ type: 'START_OVER' });
    clearPersistedWorkflow();
  }, []);
  const setError = useCallback((error: string | null) => dispatch({ type: 'SET_ERROR', error }), []);

  return {
    ctx,
    dispatch,
    clearPersistedState: clearPersistedWorkflow,
    setInputMethod,
    setNote,
    goToStep,
    toggleTemplate,
    setTemplates,
    startOver,
    setError,
    wasRestoredFromPersistence,
    pendingTemplateIdsRef,
  };
}
